import { useMemo, useState } from 'react';
import type { Farmer, Harvest } from '../../../../types';
import { getHarvestsByFarmer, getContractsByFarmer } from '../../../../store';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { EmptyState } from '../../../../components/base/EmptyState';
import { formatDate, formatNumber } from '../../../../utils/format';

interface DetailHarvestTabProps {
  farmer: Farmer;
}

const STATUS_FILTERS: Array<Harvest['status'] | 'Tất cả'> = [
  'Tất cả', 'Dự kiến', 'Nông hộ đã cập nhật', 'Chờ HTX xác nhận', 'HTX đã xác nhận', 'Sản lượng thấp', 'Hoàn thành', 'Hủy',
];

/** Full "Sản lượng" tab — promoted out of DetailProductionTab.tsx's old
 * 3-entry "Thu hoạch gần nhất" embed (Feedback round 4: "thêm phần... sản
 * lượng trong profile nông hộ"), plus a reconciliation link against the
 * linked contract's own expected output/price where one exists (same figures
 * HarvestDetailModal.tsx already computes for the HTX harvest list). */
export default function DetailHarvestTab({ farmer }: DetailHarvestTabProps) {
  const allHarvests = getHarvestsByFarmer(farmer.id).filter((h) => !h.isDeleted);
  const contracts = getContractsByFarmer(farmer.id);
  const [statusFilter, setStatusFilter] = useState<Harvest['status'] | 'Tất cả'>('Tất cả');

  const filtered = useMemo(() => {
    return allHarvests
      .filter((h) => statusFilter === 'Tất cả' || h.status === statusFilter)
      .sort((a, b) => new Date(b.ngayThuHoach || b.ngayDuKien).getTime() - new Date(a.ngayThuHoach || a.ngayDuKien).getTime());
  }, [allHarvests, statusFilter]);

  const totals = useMemo(() => {
    const confirmed = allHarvests.filter((h) => h.actualYieldConfirmed !== undefined || h.actualYield !== undefined);
    const expected = allHarvests.reduce((s, h) => s + h.soLuong, 0);
    const actual = confirmed.reduce((s, h) => s + (h.actualYieldConfirmed ?? h.actualYield ?? 0), 0);
    return { count: allHarvests.length, expected, actual, confirmedCount: confirmed.length };
  }, [allHarvests]);

  const getContractInfo = (contractId?: string) => (contractId ? contracts.find((c) => c.id === contractId) : undefined);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-slate-50 rounded-xl p-3 text-center">
          <p className="text-xs text-slate-400">Tổng lần thu hoạch</p>
          <p className="text-lg font-bold text-slate-800">{totals.count}</p>
        </div>
        <div className="bg-sky-50 rounded-xl p-3 text-center">
          <p className="text-xs text-slate-400">Dự kiến</p>
          <p className="text-sm font-bold text-sky-700">{formatNumber(totals.expected)}</p>
        </div>
        <div className="bg-agrida-50 rounded-xl p-3 text-center">
          <p className="text-xs text-slate-400">Thực tế ({totals.confirmedCount})</p>
          <p className="text-sm font-bold text-agrida-700">{formatNumber(totals.actual)}</p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto hide-scrollbar">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${statusFilter === s ? 'bg-agrida-600 border-agrida-600 text-white' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="ri-shopping-basket-line" title="Chưa có thu hoạch" description="Không có lần thu hoạch nào phù hợp với bộ lọc." />
      ) : (
        <div className="space-y-3">
          {filtered.map((h) => {
            const duKien = h.soLuong || 0;
            const actual = h.actualYieldConfirmed ?? h.actualYield ?? 0;
            const chenh = actual - duKien;
            const contract = getContractInfo(h.contractId);
            return (
              <div key={h.id} className="bg-white rounded-xl border border-slate-200 p-3 md:p-4">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-sm font-semibold text-slate-900">{h.maLanThuHoach}</p>
                  <StatusBadge status={h.status} size="sm" />
                  {h.canhBao && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-50 text-red-700 border border-red-200">{h.canhBao}</span>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs">
                  <div><span className="text-slate-400">Ngày thu:</span> <span className="font-medium text-slate-700">{h.ngayThuHoach ? formatDate(h.ngayThuHoach) : '—'}</span></div>
                  <div><span className="text-slate-400">Sản lượng:</span> <span className="font-medium text-slate-700">{formatNumber(actual || duKien)} {h.donVi}</span></div>
                  <div><span className="text-slate-400">Chênh lệch:</span> <span className={`font-medium ${chenh >= 0 ? 'text-agrida-700' : 'text-red-600'}`}>{actual > 0 && duKien > 0 ? `${chenh >= 0 ? '+' : ''}${formatNumber(chenh)} ${h.donVi}` : '—'}</span></div>
                  <div><span className="text-slate-400">Chất lượng:</span> <span className="font-medium text-slate-700">{h.chatLuong || '—'}</span></div>
                </div>
                {contract && (
                  <div className="mt-2 pt-2 border-t border-slate-100 text-xs text-slate-500">
                    Hợp đồng: <span className="font-medium text-slate-700">{contract.soHopDong}</span>
                    {contract.sanLuongDuKien && <span> · Cam kết: {formatNumber(contract.sanLuongDuKien)} kg</span>}
                    {contract.donGiaDuKien && <span> · Đơn giá: {contract.donGiaDuKien.toLocaleString('vi-VN')}đ/kg</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
